import type { LocalizedProductInfoFields } from '@shared/gql/document-nodes'
import type { AccordionItem } from '@sok/design-system'
import type { Product } from 'domain/product'
import type { ThemedObjectStyleFunction } from 'domain/styled'
import type { FC } from 'react'

import { useQuery, useReactiveVar } from '@apollo/client'
import { getConfig } from '@shared/config'
import { EuroCents } from '@shared/domain/euro-cents'
import { I18nLang as Lang } from '@shared/domain/i18n'
import { DeliveryMethod, GetDeliveryDetailsInfoDocument } from '@shared/gql/document-nodes'
import { Accordion, Col, minWidthFromTheme, Row } from '@sok/design-system'
import { CartItemContext } from 'components/CartItemContext'
import { Notification } from 'components/common/Containers/Notification'
import { PageTitle } from 'components/common/Titles'
import { ComparisonPrice } from 'components/product-price/ComparisonPrice'
import { Lowest30DayPrice, PopoverContainer } from 'components/product-price/Lowest30DayPrice'
import { ProductPrice, ProductPriceContainer } from 'components/product-price/ProductPrice'
import { UnitPrice } from 'components/product-price/UnitPrice'
import { ProductAvailabilityInfo } from 'components/ProductAvailabilityInfo'
import { ProductImage } from 'components/ProductImage'
import { ProductLabels } from 'components/ProductLabels'
import { toFormattedDate } from 'domain/date-time'
import { NO_BRAND_BRANDNAME } from 'domain/product'
import { FavoriteButtonContainer } from 'favorites/components/FavoriteButtonContainer'
import { localizationVar } from 'lib/apollo/reactive-vars'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ComplementaryProductsContextProvider } from 'services/ComplementaryProducts/complementary-products-provider'
import { pendingProductEanVar } from 'services/Delivery/cache'
import { useProductAvailabilities } from 'services/Product/hooks/use-product-availabilities'
import { useCurrentSearchProvider } from 'services/Search/hooks/use-search-provider'
import styled, { useTheme } from 'styled-components'
import { useIsLoggedIn } from 'utils/hooks/account/use-is-logged-in'
import { useAlcoholAllowedForDeliveryArea } from 'utils/hooks/delivery/use-alcohol-allowed'
import { useStoreId } from 'utils/hooks/store/use-store-id'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { trackCustomEvent } from 'utils/tracking/custom-events/custom-events'
import { trackProductDetailView } from 'utils/tracking/ecommerce'
import {
  EventAction,
  EventCategory,
  TrackingContext,
} from 'utils/tracking/interfaces/data-layer-events'
import { AddToListContainer } from 'views/shoppinglists/AddToList'

import { AddToCartWithAlerts } from './AddToCartWithAlerts'
import { NoSelectedStorePriceAlert } from './alerts'
import { BrandNameLink } from './BrandNameLink'
import { ComplementaryProducts } from './ComplementaryProducts'
import { NutrientsPerServingInfoTable } from './NutrientsPerServingInfoTable'
import { NutrientsPerUnitInfoTable } from './NutrientsPerUnitInfoTable'
import { ProductDescription } from './ProductDescription'
import { ProductFeatures } from './ProductFeatures'
import { ProductLocationTable } from './ProductLocationTable'
import { TopProductsUpsell } from './TopProductsUpsell'

const { featureFlags, domain } = getConfig()

const StyledFavoriteButtonWrapper = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1,
})

interface ProductViewProps {
  inCurrentStore?: boolean | null
  product: Product
}

export const ProductView: FC<ProductViewProps> = ({ inCurrentStore, product }) => {
  const { selectedStoreId } = useStoreId()
  const isLoggedIn = useIsLoggedIn()

  const localization = useReactiveVar(localizationVar)

  const productImageSize = 360

  const { t } = useTranslation()

  useEffect(() => {
    trackProductDetailView(product, selectedStoreId, localization)
    pendingProductEanVar(product.ean)

    return () => {
      pendingProductEanVar(null)
    }
  }, [product, selectedStoreId, localization])

  const localizedFields = localization === Lang.SV ? product?.localized?.sv : product?.localized?.fi

  const accordionItems = useAccordionItems(product, selectedStoreId, localizedFields)

  const { data: deliveryDetailsData } = useQuery(GetDeliveryDetailsInfoDocument)
  const deliveryMethodSelected = deliveryDetailsData?.deliveryDetailsInfo?.deliveryMethod
  const isHomeDelivery = deliveryMethodSelected === DeliveryMethod.HomeDelivery
  const alcoholSellingAllowedByDeliveryArea = useAlcoholAllowedForDeliveryArea()
  const alcoholSellingAllowed =
    !!alcoholSellingAllowedByDeliveryArea && deliveryMethodSelected !== DeliveryMethod.HomeDelivery

  // Loading when alcohol selling status is not yet available
  const addToCartLoading =
    !!product.isAgeLimitedByAlcohol && alcoholSellingAllowedByDeliveryArea === null

  const availabilities = useProductAvailabilities([product])?.[product.id]
  const searchProvider = useCurrentSearchProvider()

  const { isSIdEnabled } = domain

  return (
    <TrackingContextProvider
      listName={`${TrackingContext.PRODUCT_DETAIL_PAGE}: ${product.ean}`}
      trackingContext={TrackingContext.PRODUCT_DETAIL_PAGE}
      searchProvider={searchProvider}
    >
      <StyledProductCard>
        <Row>
          <Col
            span={24}
            lg={{
              span: 12,
              order: 0,
            }}
          >
            <StyledProductImageContainer>
              <ProductImage
                brand={product.brandName}
                ean={product.ean}
                height={productImageSize}
                productName={localizedFields?.name ?? product.name}
                width={productImageSize}
              />
              {isSIdEnabled && (
                <StyledFavoriteButtonWrapper>
                  <FavoriteButtonContainer ean={product.ean || ''} />
                </StyledFavoriteButtonWrapper>
              )}
            </StyledProductImageContainer>
          </Col>

          <Col data-test-id="product-page-top-area" span={24} lg={{ span: 12, order: 1 }}>
            <StyledPageTitleContainer>
              {featureFlags.productAvailabilities && availabilities && (
                <ProductAvailabilityInfo availabilities={availabilities} />
              )}

              <PageTitle data-test-id="product-name">
                {localizedFields?.name || product.name || ''}
              </PageTitle>

              {product.brandName &&
                /** @todo [VOIK-8881] this check is not needed after "Ei merkkiä" is nulled in the backend */
                product.brandName !== NO_BRAND_BRANDNAME && (
                  <BrandNameLink brandName={product.brandName} />
                )}

              <StyledProductPriceContainer>
                <ProductPrice
                  data-test-id="product-page-price"
                  approximate={!inCurrentStore}
                  product={product}
                  showDeposit
                />
              </StyledProductPriceContainer>

              {product.pricing?.campaignPriceValidUntil ? (
                <CampaignPriceValidUntil>
                  {t('Price valid until {{date}}', {
                    date: toFormattedDate(product.pricing.campaignPriceValidUntil, {
                      weekday: false,
                    }),
                  })}
                </CampaignPriceValidUntil>
              ) : null}

              {product.pricing?.lowest30DayPrice ? (
                <Lowest30DayPriceInfo>
                  <span>
                    {t('Lowest price for the 30 days before the campaign')}
                    {': '}
                  </span>
                  <Lowest30DayPriceAmount>
                    {EuroCents.fromEuros(product.pricing.lowest30DayPrice).format()}
                  </Lowest30DayPriceAmount>
                </Lowest30DayPriceInfo>
              ) : null}

              {!selectedStoreId && isSIdEnabled && <NoSelectedStorePriceAlert />}
            </StyledPageTitleContainer>

            <StyledProductLabels product={product} />

            <CartItemContext
              availabilities={availabilities}
              basicQuantityUnit={product.basicQuantityUnit || ''}
              id={product.id || ''}
              isAgeLimitedByAlcohol={product.isAgeLimitedByAlcohol ?? false}
              productName={product.name || ''}
              quantityMultiplier={product.quantityMultiplier || 1}
            >
              <AddToCartWithAlerts
                alcoholSellingAllowed={alcoholSellingAllowed}
                hasSelectedStore={!!selectedStoreId}
                inCurrentStore={!!inCurrentStore}
                isAgeLimitedByAlcohol={!!product.isAgeLimitedByAlcohol}
                isHomeDelivery={isHomeDelivery}
                loading={addToCartLoading}
              />
            </CartItemContext>

            {isLoggedIn && featureFlags.shoppingLists && (
              <AddToListContainer
                product={{
                  quantity: 1,
                  quantityUnit: product.basicQuantityUnit || '',
                  ean: product.ean || '',
                  name: product.name || '',
                }}
              />
            )}
          </Col>
        </Row>

        <StyledAccordionContainer>
          <Accordion items={accordionItems} />
        </StyledAccordionContainer>
      </StyledProductCard>

      {product.ean ? (
        <ComplementaryProductsContextProvider focusOnEan={product.ean}>
          <ComplementaryProducts focusOnEan={product.ean} />
        </ComplementaryProductsContextProvider>
      ) : null}

      {product.hierarchyPath && product.hierarchyPath.length > 0 ? (
        <TopProductsUpsell
          categoryName={product.hierarchyPath[0].name || null}
          categorySlug={product.hierarchyPath[0].slug || null}
          ean={product.ean}
          includeAlcohol={!!product.isAgeLimitedByAlcohol}
        />
      ) : null}
    </TrackingContextProvider>
  )
}

const PRODUCT_INFO = 'product-info'
const NUTRIENTS_INFO = 'nutrients-info'
const LOCATION_INFO = 'product-location-info'
const FEATURES_INFO = 'product-features-info'

const accordionIdToAction: Record<string, EventAction> = {
  [PRODUCT_INFO]: EventAction.OPEN_PRODUCT_INFO_TAB,
  [NUTRIENTS_INFO]: EventAction.OPEN_PRODUCT_NUTRIENTS_TAB,
  [LOCATION_INFO]: EventAction.OPEN_PRODUCT_SHELF_LOCATION_TAB,
  [FEATURES_INFO]: EventAction.OPEN_PRODUCT_FEATURES_TAB,
}

const useAccordionItems = (
  product: NonNullable<Product>,
  selectedStoreId: string,
  localizedFields?: LocalizedProductInfoFields | null,
): AccordionItem[] => {
  const { t } = useTranslation()
  const theme = useTheme()

  const handleClick = useCallback<NonNullable<AccordionItem['onClick']>>(
    (_, item) => {
      const action = accordionIdToAction[item.id]
      /** When opening an accordion item, it will be closed while clicking */
      if (!item.open && action && product.ean) {
        trackCustomEvent({ category: EventCategory.PRODUCT_PAGES, action, label: product.ean })
      }
    },
    [product.ean],
  )

  return useMemo(() => {
    const accordionItems: AccordionItem[] = [
      {
        id: PRODUCT_INFO,
        title: t('Product info'),
        'data-test-id': PRODUCT_INFO,
        onClick: handleClick,
        open: false,
        content: (
          <div data-test-id={`${PRODUCT_INFO}-content`}>
            <ProductDescription product={product} localizedFields={localizedFields} />
            <Notification
              color={theme.color.backgroundEmphasis}
              text={t('Product info description')}
            />
          </div>
        ),
      },
      // @TODO [VOIK-2139] Use this when data is available in gql data
      // { id: '1', title: 'Ominaisuudet', content: 'content' },
    ]

    const hasProductFeatures =
      localizedFields?.userGuideForConsumer || localizedFields?.storageGuideForConsumer

    if (hasProductFeatures) {
      accordionItems.push({
        id: FEATURES_INFO,
        title: t('Product features info'),
        'data-test-id': FEATURES_INFO,
        onClick: handleClick,
        open: false,
        content: (
          <div data-test-id={`${FEATURES_INFO}-content`}>
            <ProductFeatures
              userGuideForConsumer={localizedFields.userGuideForConsumer}
              storageGuideForConsumer={localizedFields.storageGuideForConsumer}
            />
          </div>
        ),
      })
    }

    const hasNutrientsPerUnitInfo = product.nutrients && product.nutrients.length > 0
    const hasNutrientsPerServingInfo =
      product.nutrientsPerServing && product.nutrientsPerServing.length > 0

    if (hasNutrientsPerUnitInfo || hasNutrientsPerServingInfo) {
      accordionItems.push({
        id: NUTRIENTS_INFO,
        title: t('Nutrient info'),
        'data-test-id': NUTRIENTS_INFO,
        onClick: handleClick,
        open: false,
        content: (
          <>
            {hasNutrientsPerUnitInfo && (
              <StyledNutrientsPerUnitInfoContainer
                data-test-id={`${NUTRIENTS_INFO}-per-unit-content`}
              >
                <NutrientsPerUnitInfoTable nutrients={product.nutrients || []} />
                <Notification
                  color={theme.color.backgroundEmphasis}
                  text={t('Nutrient info description')}
                />
              </StyledNutrientsPerUnitInfoContainer>
            )}
            {hasNutrientsPerServingInfo && (
              <div data-test-id={`${NUTRIENTS_INFO}-per-serving-content`}>
                <NutrientsPerServingInfoTable nutrients={product.nutrientsPerServing || []} />
              </div>
            )}
          </>
        ),
      })
    }

    if (`${product.storeId}` === selectedStoreId && product.location?.aisle) {
      accordionItems.push({
        id: LOCATION_INFO,
        title: t('Product location info'),
        'data-test-id': LOCATION_INFO,
        onClick: handleClick,
        open: false,
        content: (
          <div data-test-id={`${LOCATION_INFO}-content`}>
            <ProductLocationTable
              aisle={product.location.aisle}
              floor={product.location.floor}
              storeId={selectedStoreId}
            />
          </div>
        ),
      })
    }

    return accordionItems
  }, [t, handleClick, product, localizedFields, theme.color.backgroundEmphasis, selectedStoreId])
}

const StyledProductLabels = styled(ProductLabels).attrs({ size: 24 })(({ theme }) => ({
  [minWidthFromTheme(theme).tablet]: {
    marginBottom: theme.spacings.small,
  },
}))

const StyledAccordionContainer = styled.div`
  padding-top: 1.5rem;
`

const StyledPageTitleContainer = styled.div(({ theme }) => ({
  marginTop: 20,
  marginBottom: theme.spacings.medium,

  h1: {
    marginTop: 20,
    marginBottom: theme.spacings.xxSmall,
  },

  [BrandNameLink]: {
    display: 'block',
    marginBottom: theme.spacings.medium,
  },

  [NoSelectedStorePriceAlert]: {
    marginTop: theme.spacings.medium,
  },

  [minWidthFromTheme(theme).tablet]: {
    marginTop: 0,
  },
}))

const StyledProductCard = styled.div`
  overflow-x: hidden;
  background-color: ${({ theme }) => theme.colors.white};

  > div:first-child,
  ${TopProductsUpsell} {
    ${({ theme }) => minWidthFromTheme(theme).tablet} {
      padding: 0;
    }
    padding: 16px 20px 16px 20px;
  }

  ${({ theme }) => minWidthFromTheme(theme).tablet} {
    padding: ${({ theme }) => theme.spacings.xxLarge} ${({ theme }) => theme.spacings.xxxLarge};
  }

  ${TopProductsUpsell} {
    margin-top: ${({ theme }) => theme.spacings.xLarge};
  }
`

const StyledProductImageContainer = styled.div`
  position: relative;
  text-align: center;
  display: flex;
  justify-content: center;
  width: 100%;

  ${({ theme }) => minWidthFromTheme(theme).tablet} {
    max-width: 287px;
    max-height: 287px;
    margin-left: auto;
    margin-right: auto;
  }
`

const StyledProductPriceContainer = styled.div({
  display: 'flex',
  width: 1000,

  [PopoverContainer]: {
    display: 'none',
  },
  [UnitPrice]: {
    fontSize: 27,
  },
  [Lowest30DayPrice]: {
    display: 'none',
  },

  [ProductPriceContainer]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    [ComparisonPrice]: {
      paddingLeft: 0,
    },
  },
})

const Lowest30DayPriceAmount = styled.span({
  textDecoration: 'line-through',
})

const StyledNutrientsPerUnitInfoContainer = styled.div(({ theme }) => ({
  marginBottom: theme.spacings.xLarge,
}))

const smallCampaignText: ThemedObjectStyleFunction = ({ theme }) => ({
  ...theme.sizes.xxs,
  color: '#666666',
})

const Lowest30DayPriceInfo = styled.p(smallCampaignText, {
  [ProductPrice]: {
    [UnitPrice]: {
      display: 'none',
    },
    [PopoverContainer]: {
      display: 'none',
    },
  },
})

const CampaignPriceValidUntil = styled.p(smallCampaignText, ({ theme }) => ({
  paddingTop: theme.spacings.xxxSmall,
}))
